import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZSUyMG1vZGVybiUyMHdoaXRlfGVufDB8fHx8MTc4MjQ2NjMwMXww&ixlib=rb-4.1.0&q=85"
          alt="Corporate"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-700/85 via-orange-600/70 to-orange-500/50" />
        <div className="relative z-10 max-w-md px-12 text-white">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-white text-orange-600 flex items-center justify-center font-bold heading">GR</div>
            <div className="heading text-xl font-bold">Golden Rolls</div>
          </div>
          <h1 className="heading text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Customer Return Management
          </h1>
          <p className="text-white/90 leading-relaxed">
            End-to-end RMA, quality inspection, and credit note issuance — built for manufacturing operations.
          </p>
          <ul className="mt-8 space-y-2 text-sm text-white/85">
            <li>· Track every return from dispatch to closure</li>
            <li>· Quality inspection with full audit trail</li>
            <li>· Credit notes & reports in one place</li>
          </ul>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md brand-gradient flex items-center justify-center text-white font-bold heading">GR</div>
            <div className="heading text-xl font-bold text-slate-900">Golden Rolls</div>
          </div>
          <h2 className="heading text-3xl font-bold text-slate-900">Welcome back</h2>
          <p className="text-slate-500 mt-1">Sign in to your RMA workspace</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@goldenrolls.com"
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-1.5"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Sign in
            </Button>
            <div className="text-center text-sm text-slate-500">
              <Link to="/forgot-password" className="text-slate-600 hover:text-orange-700" data-testid="forgot-link">
                Forgot password?
              </Link>
            </div>
            <div className="text-center text-sm text-slate-500">
              New customer?{" "}
              <Link to="/register" className="font-medium text-orange-700 hover:underline" data-testid="register-link">
                Create an account
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
